import { useEffect } from 'react'
import { useModalContext } from '@sketch/components'

import { localStorageKeys } from '@sketch/constants'

import { getParsedItem } from '@sketch/utils'

import WorkspaceWelcomeModal from '../../modals/WorkspaceWelcomeModal'
import { useCanSubscribeViaStripe } from '../../utils'

import {
  WorkspaceMinimalFragment,
  useGetPaymentDetailsQuery,
  BillingMinimalFragment,
} from '@sketch/gql-types'
import { useUserProfile } from '@sketch/modules-common'

/**
 * Renders a Welcome Modal for Workspaces
 * Allows the user to subscribe right away or keep
 * using the Workspace as a Trial.
 *
 * Will only render if:
 *  - User is Owner of the Workspace
 *  - Was not dismissed before
 *  - Workspace is on trial
 *  - Workspace is not subscribed
 *  - User is not on a trial on the App Store
 */
const useWelcomeModal = (
  workspace: WorkspaceMinimalFragment,
  subscriptionDetails: Pick<BillingMinimalFragment, 'status'>
) => {
  const { showModal } = useModalContext()

  const { data: userData } = useUserProfile()

  const { data } = useGetPaymentDetailsQuery({
    variables: { customerId: workspace.customer?.identifier! },
    skip: !workspace.customer?.identifier,
  })

  const { canSubscribeStripe, customerProvider } = useCanSubscribeViaStripe(
    workspace.identifier,
    workspace.customer?.identifier
  )

  const isSubscribed = data?.customer.paymentDetails.type !== 'NONE'
  const isTrial =
    customerProvider?.customer.subscriptionInfo.status === 'TRIALING'
  const isWebSignup = userData?.me.isWebSignup

  const wasWelcomeModalDismissed = getParsedItem(
    localStorageKeys.dismissedWelcomeModal
  )?.includes(workspace.identifier)

  const shouldRenderWelcomeModal =
    isWebSignup &&
    workspace.userIsOwner &&
    !wasWelcomeModalDismissed &&
    isTrial &&
    !isSubscribed &&
    canSubscribeStripe

  /** Only render the modal according to the conditions above*/
  useEffect(() => {
    if (shouldRenderWelcomeModal) {
      showModal(WorkspaceWelcomeModal, {
        workspaceId: workspace.identifier,
        customerId: workspace.customer?.identifier!,
      })
    }
  }, [showModal, shouldRenderWelcomeModal, workspace])
}

export default useWelcomeModal
